import classNames from "classnames";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { CSSProperties, DetailedHTMLProps, InputHTMLAttributes, useEffect, useId, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";
import { DateTime } from "ts-luxon";

import { useBreakpoint } from "~/contexts/breakpoint";
import IntlUtils from "~/utils/intl-utils";

import styles from "./input-field-date.module.scss";

const InputFieldDateMobile = dynamic(() => import("./input-field-date-mobile"), {
  ssr: false,
  loading: () => <input type="text" className={styles.inputDate} readOnly />,
});
const InputFieldDateDesktop = dynamic(() => import("./input-field-date-desktop"), {
  ssr: false,
  loading: () => <input type="text" className={styles.inputDate} readOnly />,
});

type InputDateProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  label: string;
  isOptional?: boolean;
  displayDateFormat?: string;
  minimumDate?: DateTime | Date;
  maximumDate?: DateTime | Date;
  error?: string;
  onClearErrors?: () => void;
  control?: Control<any>;
  name: string;
};

export default function InputFieldDateMui({
  label,
  isOptional,
  displayDateFormat = IntlUtils.defaultDateFormat,
  minimumDate,
  maximumDate,
  error,
  onClearErrors,
  control,
  className,
  name,
  ...rest
}: InputDateProps) {
  const t = useTranslations();
  const id = useId();
  const breakpoint = useBreakpoint();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const refLabel = useRef<HTMLLabelElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();

  const minDate = minimumDate instanceof Date ? DateTime.fromJSDate(minimumDate) : (minimumDate as DateTime);
  const maxDate = !maximumDate
    ? DateTime.fromJSDate(new Date())
    : maximumDate instanceof Date
    ? DateTime.fromJSDate(maximumDate)
    : maximumDate;

  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  useEffect(() => {
    setWidthLabel(refLabel.current?.offsetWidth);
  }, [isOptional]);

  return (
    <div
      className={classNames(
        styles.container,
        error && styles.containerWithErrors,
        isInputFocused && styles.containerInFocus,
        className
      )}
    >
      <div className={styles.wrapperLabel}>
        <label
          ref={refLabel}
          htmlFor={id}
          onMouseDown={(e) => e.preventDefault()}
          style={styleLabelLine}
          className={styles.label}
        >
          {label}
          {!isOptional && (
            <span className={styles.mandatory} aria-hidden>
              {t("generic.form.mandatory_short")}
            </span>
          )}
        </label>
      </div>
      <div className={styles.wrapperInput}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, onBlur, value, ref } }) => {
            const Component = breakpoint === "desktop" ? InputFieldDateDesktop : InputFieldDateMobile;
            return (
              <Component
                name={name}
                minimumDate={minDate}
                maximumDate={maxDate}
                {...rest}
                value={value}
                id={id}
                onChange={onChange}
                onFocus={() => {
                  onClearErrors?.();
                  setIsInputFocused?.(true);
                }}
                onBlur={() => {
                  onBlur();
                  setIsInputFocused?.(false);
                }}
                inputRef={ref}
                onClearErrors={onClearErrors}
              />
            );
          }}
        />
      </div>
      {error && (
        <p id={`${id}-error`} className={styles.errorMessage}>
          {error}
        </p>
      )}
    </div>
  );
}
